import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { ReduxState } from '../types';
import { setInterval, setSelectedInterval } from '../actions/graphsFilterActions';

type TimeInterval = '1-day' | '1-week' | '2-weeks' | '1-month' | '3-months' | '6-months' | null;

interface UseChartFilterReturn {
  tempStartDate: string;
  tempEndDate: string;
  selectedInterval: string | null;
  setTempStartDate: (date: string) => void;
  setTempEndDate: (date: string) => void;
  handleApplyDates: () => void;
  handleIntervalSelect: (interval: TimeInterval) => void;
}

const useChartFilter = (): UseChartFilterReturn => {
  const dispatch = useDispatch();
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedInterval = useSelector((state: ReduxState) => state.graphsFilter.selectedInterval);
  const [isInitialized, setIsInitialized] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(interval?.startDate?.split('T')[0] || '');
  const [tempEndDate, setTempEndDate] = useState(interval?.endDate?.split('T')[0] || '');
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlDate = useMemo(() => searchParams.get('date'), [searchParams]);

  const { urlStartDate, urlEndDate } = useMemo(() => {
    if (!urlDate) return { urlStartDate: '', urlEndDate: '' };
    const [day, month, year] = urlDate.split('-');
    const urlDateDayjs = dayjs(`${year}-${month}-${Number(day)}`);
    const isoDateStartDate = urlDateDayjs.subtract(3, 'day').format('YYYY-MM-DDTHH:mm:ss');
    const isoDateEndDate = urlDateDayjs.add(3, 'day').format('YYYY-MM-DDTHH:mm:ss');
    return { urlStartDate: isoDateStartDate, urlEndDate: isoDateEndDate };
  }, [urlDate]);

  useEffect(() => {
    if (!isInitialized) {
      const storedStartDate = sessionStorage.getItem('chartFilterStartDate');
      const storedEndDate = sessionStorage.getItem('chartFilterEndDate');

      // Get the current date in LOCAL time
      const currentDate = dayjs();
      const intervalEndDate = currentDate.endOf('day');
      const intervalStartDate = intervalEndDate.subtract(14, 'day').startOf('day');
      const defaultStartDateIso = intervalStartDate.format('YYYY-MM-DDTHH:mm:ss');
      const defaultEndDateIso = intervalEndDate.format('YYYY-MM-DDTHH:mm:ss');

      if (urlStartDate && urlEndDate) {
        // Only dispatch if the interval is different
        if (!interval || !dayjs(interval?.startDate).isSame(dayjs(urlStartDate))
          || !dayjs(interval?.endDate).isSame(dayjs(urlEndDate))) {
          dispatch(setInterval({ startDate: urlStartDate, endDate: urlEndDate }));
          dispatch(setSelectedInterval(null));
        }
        setTempStartDate(urlStartDate.split('T')[0]);
        setTempEndDate(urlEndDate.split('T')[0]);
        dispatch(setSelectedInterval(null));
      } else if (storedStartDate && storedEndDate) {
        // Only dispatch if the interval is different
        if (!interval || dayjs(interval.startDate).format('YYYY-MM-DD') !== dayjs(storedStartDate).format('YYYY-MM-DD')
            || dayjs(interval.endDate).format('YYYY-MM-DD') !== dayjs(storedEndDate).format('YYYY-MM-DD')) {
          dispatch(setInterval({ startDate: storedStartDate, endDate: storedEndDate }));
        }
        setTempStartDate(storedStartDate.split('T')[0]);
        setTempEndDate(storedEndDate.split('T')[0]);
        dispatch(setSelectedInterval(null));
      } else if (interval) {
        setTempStartDate(interval.startDate?.split('T')[0] || '');
        setTempEndDate(interval.endDate?.split('T')[0] || '');
      } else {
        // Only dispatch if there's no interval set
        dispatch(setInterval({ startDate: defaultStartDateIso, endDate: defaultEndDateIso }));
        setTempStartDate(defaultStartDateIso.split('T')[0]);
        setTempEndDate(defaultEndDateIso.split('T')[0]);
        dispatch(setSelectedInterval('2-weeks'));
      }
      setIsInitialized(true);
    }
  }, [dispatch, isInitialized, interval, urlStartDate, urlEndDate]);

  const handleApplyDates = () => {
    dispatch(setSelectedInterval(null));
    const formattedStartDate = dayjs(tempStartDate).utc().startOf('day').format('YYYY-MM-DDTHH:mm:ss');
    const formattedEndDate = dayjs(tempEndDate).utc().endOf('day').format('YYYY-MM-DDTHH:mm:ss');

    // Only dispatch if the interval is different
    if (!interval || interval.startDate !== formattedStartDate
        || interval.endDate !== formattedEndDate) {
      dispatch(setInterval({ startDate: formattedStartDate, endDate: formattedEndDate }));
    }

    const params = new URLSearchParams(location.search);
    params.delete('date');
    history.replace({ search: params.toString() });
  };

  const handleIntervalSelect = (intervalSelected: TimeInterval) => {
    dispatch(setSelectedInterval(intervalSelected));

    // Always set the end date to today at 23:59:59 in LOCAL TIME
    const intervalEndDate = dayjs().endOf('day');

    // Determine the start date based on the selected interval
    let intervalStartDate = intervalEndDate.clone(); // Clone to avoid mutation

    switch (intervalSelected) {
      case '1-day':
        intervalStartDate = intervalEndDate.startOf('day');
        break;
      case '1-week':
        intervalStartDate = intervalEndDate.subtract(7, 'day').startOf('day');
        break;
      case '2-weeks':
        intervalStartDate = intervalEndDate.subtract(14, 'day').startOf('day');
        break;
      case '1-month':
        intervalStartDate = intervalEndDate.subtract(1, 'month').startOf('day');
        break;
      case '3-months':
        intervalStartDate = intervalEndDate.subtract(3, 'month').startOf('day');
        break;
      case '6-months':
        intervalStartDate = intervalEndDate.subtract(6, 'month').startOf('day');
        break;
      default:
        return;
    }

    // Convert to ISO strings while keeping local time
    const startDateIso = intervalStartDate.format('YYYY-MM-DDTHH:mm:ss');
    const endDateIso = intervalEndDate.format('YYYY-MM-DDTHH:mm:ss');

    // Only dispatch if the interval is different
    if (!interval || interval.startDate !== startDateIso || interval.endDate !== endDateIso) {
      sessionStorage.setItem('chartFilterStartDate', startDateIso);
      sessionStorage.setItem('chartFilterEndDate', endDateIso);
      dispatch(setInterval({ startDate: startDateIso, endDate: endDateIso }));
    }

    // Update the TextField dates
    setTempStartDate(intervalStartDate.format('YYYY-MM-DD'));
    setTempEndDate(intervalEndDate.format('YYYY-MM-DD'));
  };

  return {
    tempStartDate,
    tempEndDate,
    selectedInterval,
    setTempStartDate,
    setTempEndDate,
    handleApplyDates,
    handleIntervalSelect,
  };
};

export default useChartFilter;
